import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
} from "framer-motion";
import { CalendarCheck, ArrowDown } from "lucide-react";
import { CALENDLY_URL } from "@/components/Navbar";
import { PhoneMockup } from "@/components/PhoneMockup";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const Avatar = ({ initials, color }) => (
  <span
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-ink ${color}`}
  >
    {initials}
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // Inclinaison 3D du téléphone suivant la souris
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 120,
    damping: 18,
  });
  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="accueil"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative overflow-hidden bg-[linear-gradient(115deg,#2BC97E_0%,#0BC1D6_55%,#00C4CC_100%)] pt-28 pb-20 lg:pt-36 lg:pb-24"
      data-testid="hero-section"
    >
      {/* Bulles douces, clin d'œil au logo */}
      <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute right-1/3 top-32 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-white/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
        <div className="text-white lg:col-span-7">
          <FadeUp delay={0.1}>
            <span className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-white/85">
              <span className="h-px w-10 bg-white/70" />
              Syndicappli360 — L'application de la copropriété
            </span>
          </FadeUp>

          <h1 className="mt-7 text-[clamp(2.8rem,6.8vw,5.8rem)] font-bold leading-[1.02] tracking-tight text-white">
            <Line delay={0.2}>Tous vos outils,</Line>
            <Line delay={0.32}>
              <span className="font-normal text-white/90">une seule appli,</span>
            </Line>
            <Line delay={0.44}>
              <span className="font-extrabold">à 360°.</span>
            </Line>
          </h1>

          <FadeUp delay={0.62}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
              Facilitez-vous la Vie en Copropriété à 360°. De la messagerie à la
              comptabilité, des AG en visioconférence aux votes sécurisés :
              tous les besoins du gestionnaire, réunis dans une seule
              application. Gagnez du temps et de l'argent, adoptez Syndicappli.
            </p>
          </FadeUp>

          <FadeUp delay={0.74} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-demo-button"
              className="group flex items-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-bold text-ink shadow-[0_20px_45px_-15px_rgba(0,60,70,0.55)] transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <CalendarCheck size={18} className="text-electric transition-transform duration-300 group-hover:-rotate-12" />
              Réserver une démo
            </a>
            <a
              href="#fonctionnalites"
              data-testid="hero-cta-features-button"
              className="flex items-center gap-3 rounded-full border border-white/60 px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:scale-105 hover:bg-white/10 active:scale-95"
            >
              Découvrir les fonctionnalités
              <ArrowDown size={16} />
            </a>
          </FadeUp>

          <FadeUp delay={0.86}>
            <p className="mt-9 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
              Disponible sur iOS, Android &amp; web — smartphone, PC, Mac, tablette
            </p>
          </FadeUp>
        </div>

        {/* Simulation iPhone — les écrans de l'application, inclinables à la souris */}
        <motion.div style={{ y: yCard }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            style={{ perspective: 1200 }}
          >
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
              <PhoneMockup />
            </motion.div>
          </motion.div>

          {/* Badge calendrier, comme l'écran Accueil de l'app */}
          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="absolute right-0 top-10 flex items-center gap-3 rounded-full bg-white py-2.5 pl-2.5 pr-5 shadow-[0_20px_45px_-15px_rgba(0,55,70,0.5)] lg:-right-6"
            data-testid="hero-badge-calendar"
          >
            <span className="flex h-10 w-10 flex-col items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white">
              <span className="text-[8px] font-semibold uppercase leading-none opacity-80">Avril</span>
              <span className="text-sm font-bold leading-none">8</span>
            </span>
            <span>
              <span className="block text-sm font-semibold text-ink">Assemblée Générale</span>
              <span className="block text-[10px] uppercase tracking-wider text-ink/40">En visioconférence à 360°</span>
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
